import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEnquiryById, selectEnquiryLoading } from 'redux/enquiries';
import { selectEnquiries, loadMoreLoader } from 'redux/enquiries';
import { selectScreenSize } from 'redux/session/selectors';
import { SCREEN_SIZE } from 'types';
import { DataTableBasicViewComponent } from 'components/DataTableBasicView';
import MobileDataTable from './mobileDataTable';
import { LoadMoreComponent } from 'components/LoadMore';
import { Button, InlineLoading, Loading, Tag } from 'carbon-components-react';
import { TextWrap16 } from '@carbon/icons-react';
import { EmptyStatePage } from 'components';
import NoRowData from '../NoRowData';
import { uniqBy } from 'lodash';

export const ListView: React.FC<Props> = ({
  isFullSet,
  enquiryList,
  setEnquiryList,
  setOrderBy,
  selectedId,
  loadMoreData,
  isPannelExpanded
}: any) => {
  // Initialisation
  const table = {
    headerData: isFullSet
      ? [
          { key: 'jobId', header: 'Job', isSortable: true },
          { key: 'address', header: 'Address' },
          { key: 'enquirer', header: 'Enquirer', isSortable: true },
          { key: 'responsesReceived', header: 'Responses (Rec/Total)' }
        ]
      : [
          { key: 'jobId', header: 'Job', isSortable: true },
          { key: 'userRef', header: 'User reference', isSortable: true },
          { key: 'address', header: 'Address' },
          { key: 'status', header: 'Status' }
        ]
  };
  const dispatch = useDispatch();
  // SELECTORS
  const isLoading: boolean = useSelector(selectEnquiryLoading);
  const screenSize = useSelector(selectScreenSize);
  const loader: any = useSelector(loadMoreLoader);
  const enquiries: any = useSelector(selectEnquiries);
  const [sortingHeader, setSortingHeader] = useState<string>('jobId');
  const [sortDirection, setSortDirection] = useState<string>('DESC');
  const [selectedRowId, setSelectedRowId] = useState<string>('');

  const onClickHeader = (key: string) => {
    const direction =
      key === sortingHeader
        ? sortDirection === 'DESC'
          ? 'ASC'
          : 'DESC'
        : 'DESC';
    let order = '';
    switch (key) {
      case 'jobId': {
        order = `${direction === 'DESC' ? '-' : ''}createdAt`;
        break;
      }
      case 'userRef': {
        order = `${direction === 'DESC' ? '-' : ''}userReference`;
        break;
      }
      case 'enquirer': {
        order = `${direction === 'DESC' ? '-' : ''}requestorFirstName,${
          direction === 'DESC' ? '-' : ''
        }requestorLastName`;
        break;
      }
    }
    setSortingHeader(key);
    setOrderBy(order);
    setSortDirection(direction);
  };

  const isSandboxTestTag = () => <Tag type="purple">Sandbox</Tag>;

  useEffect(() => {
    if (enquiries) {
      // eslint-disable-next-line
      let newEnquiryList = uniqBy([...enquiryList, ...enquiries.items], 'id');
      newEnquiryList = newEnquiryList.map((item) => {
        let statusObj = null;
        if (item.isSandboxTest && typeof item.status === 'string') {
          statusObj = {
            status: item.status,
            isSandboxTest: isSandboxTestTag()
          };
        }

        return {
          ...item,
          status: statusObj || item.status
        };
      });

      // eslint-disable-next-line
      setEnquiryList(newEnquiryList);
    }
    // eslint-disable-next-line
  }, [enquiries]);

  useEffect(() => {
    if (!isPannelExpanded) {
      setSelectedRowId('');
    }
  }, [isPannelExpanded]);

  const enquiryDetailsHandler = useCallback(
    (selectRowObj: any) => {
      const { id, cells } = selectRowObj;
      const cell0Value = cells[0]?.value;
      cell0Value && selectedId(cell0Value?.job || '');
      id && dispatch(fetchEnquiryById({ enquiryId: id, logo: true }));
      setSelectedRowId(id);
    },
    [selectedId, dispatch]
  );
  return (
    <div
      id="enqiryHistoryListView"
      className="history-page--list-view"
      data-testid="enqiryHistoryListView"
    >
      {!isLoading && !(loader && enquiryList.length === 0) ? (
        <>
          {enquiries?.count > 0 ? (
            <>
              {screenSize !== SCREEN_SIZE.SM ? (
                <DataTableBasicViewComponent
                  size="tall"
                  isLoading={isLoading}
                  headerData={table.headerData}
                  rowData={enquiryList}
                  title={''}
                  description={''}
                  selectedRowHandler={enquiryDetailsHandler}
                  onClickHeader={onClickHeader}
                  sortingHeader={sortingHeader}
                  sortDirection={sortDirection}
                  selectedRowId={selectedRowId}
                />
              ) : (
                <MobileDataTable
                  isFullSet={isFullSet}
                  size="tall"
                  headers={table.headerData}
                  rows={enquiryList}
                  title={''}
                  description={''}
                  selectedRowHandler={enquiryDetailsHandler}
                />
              )}
              {enquiries?.count > 10 &&
                enquiryList?.length !== enquiries?.count && (
                  <LoadMoreComponent pos="bottom">
                    <div className="history-page--load-more-action">
                      {loader ? (
                        <InlineLoading
                          className="loader"
                          description="Loading..."
                        />
                      ) : (
                        <Button
                          renderIcon={TextWrap16}
                          kind="primary"
                          onClick={() => loadMoreData()}
                        >
                          Load more
                        </Button>
                      )}
                    </div>
                  </LoadMoreComponent>
                )}
            </>
          ) : (
            <EmptyStatePage>
              <NoRowData />
            </EmptyStatePage>
          )}
        </>
      ) : (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  isFullSet: PropTypes.bool,
  enquiryList: PropTypes.array.isRequired,
  setEnquiryList: PropTypes.func.isRequired,
  setOrderBy: PropTypes.func.isRequired,
  selectedId: PropTypes.func.isRequired,
  loadMoreData: PropTypes.func.isRequired,
  isPannelExpanded: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

ListView.propTypes = propTypes;

ListView.defaultProps = {
  isFullSet: false,
  selectedId: () => {},
  loadMoreData: () => {},
  isPannelExpanded: false
};
export default React.memo(ListView);
